import { Component, Input, Output, EventEmitter, AfterViewInit, ElementRef, OnChanges, OnDestroy, SimpleChanges, PLATFORM_ID, Inject, inject, signal, ChangeDetectorRef, ApplicationRef, createComponent, ComponentRef, EnvironmentInjector } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { MarkdownModule } from 'ngx-markdown';
import type TurndownService from 'turndown';
import { resolveWikilinks, extractWikilinkSlugs, ARTICLE_ROUTE_PREFIX, ResolvedArticle, formatSlugAsTitle, buildWikilink } from '../../../shared/utils/wikilink.utils';
import { ArticleService } from '../../../shared/services/article.service';
import { PointsApiService } from '../../../shared/services/points-api.service';
import { materialsState } from '../../../state/materials.state';
import { WikilinkPopupComponent, WikilinkPopupData, WikilinkChange } from '../wikilink-popup/wikilink-popup.component';
import { SwNavigatorService } from '../../../shared/services/sw-navigator.service';
import { RecyclingPointCardComponent } from '../recycling-point-card/recycling-point-card.component';
import { MaterialCardComponent } from '../material-card/material-card.component';
import { ArticleCardComponent } from '../article-card/article-card.component';

@Component({
  selector: 'app-markdown-viewer',
  standalone: true,
  imports: [CommonModule, MarkdownModule, WikilinkPopupComponent],
  templateUrl: './markdown-viewer.component.html',
  styleUrls: ['./markdown-viewer.component.scss']
})
export class MarkdownViewerComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() set data(value: string) {
    // Only update if not currently editing
    if (!this.isEditing) {
      this._rawData = value;
      this.resolveAndSetData(value);
    }
  }
  get data(): string {
    return this._resolvedData;
  }
  private _rawData: string = '';
  private _resolvedData: string = '';

  /** Pre-resolved article map for wikilinks (optional, will auto-resolve if not provided) */
  @Input() articleMap: Map<string, ResolvedArticle> | null = null;

  /** Whether to resolve wikilinks in the content */
  @Input() resolveLinks: boolean = true;

  @Input() collapsibleHeaders: boolean = true;
  @Input() openLinksInNewTab: boolean = true;
  @Input() editable: boolean = false;
  @Output() linkClick = new EventEmitter<string>();
  @Output() contentChange = new EventEmitter<string>();
  /** Emitted when a wikilink to a non-existing article is clicked */
  @Output() wikilinkCreate = new EventEmitter<string>();

  /** Active wikilink popup data */
  popupData = signal<WikilinkPopupData | null>(null);

  private readonly router = inject(Router);
  private readonly articleService = inject(ArticleService);
  private readonly pointsApiService = inject(PointsApiService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly swnav = inject(SwNavigatorService);
  private readonly appRef = inject(ApplicationRef);
  private readonly envInjector = inject(EnvironmentInjector);

  private turndownService: TurndownService | null = null;
  private isEditing = false;
  private isBrowser: boolean;
  private entityCache = new Map<string, any>();
  private _embedTimer: any;
  private dynamicRefs: ComponentRef<any>[] = [];

  constructor(private elementRef: ElementRef, @Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnDestroy(): void {
    clearTimeout(this._embedTimer);
    this.destroyDynamicComponents();
  }

  private destroyDynamicComponents(): void {
    for (const ref of this.dynamicRefs) {
      this.appRef.detachView(ref.hostView);
      ref.destroy();
    }
    this.dynamicRefs = [];
  }

  /**
   * Resolve wikilinks in raw markdown and update rendered data.
   * Always resolves immediately (unknown slugs shown as broken links),
   * then upgrades when the backend responds.
   */
  private resolveAndSetData(rawMarkdown: string): void {
    if (!rawMarkdown) {
      this._resolvedData = rawMarkdown;
      return;
    }

    // Step 1: Resolve internal links (URL-based embeds)
    let processed = this.resolveInternalLinks(rawMarkdown);

    if (!this.resolveLinks) {
      this._resolvedData = processed;
      this.scheduleEmbedPopulation();
      return;
    }

    const slugs = extractWikilinkSlugs(processed);

    if (slugs.length === 0) {
      this._resolvedData = processed;
      this.scheduleEmbedPopulation();
      return;
    }

    if (this.articleMap && this.articleMap.size > 0) {
      this._resolvedData = resolveWikilinks(processed, this.articleMap);
      this.scheduleEmbedPopulation();
      return;
    }

    // Build an immediate map from cache + fallback for uncached slugs
    const cached = this.articleService.getCachedSlugMap();
    const immediateMap = new Map<string, ResolvedArticle>();
    let hasUncached = false;

    for (const slug of slugs) {
      const entry = cached.get(slug);
      if (entry) {
        immediateMap.set(slug, entry);
      } else {
        immediateMap.set(slug, { slug, title: formatSlugAsTitle(slug), exists: false });
        hasUncached = true;
      }
    }

    this._resolvedData = resolveWikilinks(processed, immediateMap);
    this.scheduleEmbedPopulation();

    if (hasUncached) {
      this.articleService.lookupSlugs(slugs).subscribe({
        next: (articleMap) => {
          this._resolvedData = resolveWikilinks(processed, articleMap);
          this.scheduleEmbedPopulation();
          this.cdr.markForCheck();
        },
        error: () => {
          // Keep the immediate resolution (broken-link style) on error
        }
      });
    }
  }

  /**
   * Detect markdown links pointing to internal routes and transform them
   * into styled embed elements.
   * 
   * Matches patterns:
   *   [text](http://localhost:PORT/home/slug)
   *   [text](https://remateri.ar/home/slug)
   *   [text](/home/slug)
   *   Same for /material/slug and /articulo/slug
   */
  private resolveInternalLinks(markdown: string): string {
    // Match markdown links with internal URLs
    const internalLinkRegex = /\[([^\]]*)\]\(((?:https?:\/\/(?:localhost:\d+|(?:www\.)?remateri\.ar))?)\/(home|material|articulo)\/([a-z0-9][a-z0-9-]*[a-z0-9]|[a-z0-9])\)/gi;

    return markdown.replace(internalLinkRegex, (_match, linkText: string, _domain: string, routeType: string, slug: string) => {
      const type = routeType.toLowerCase();
      const path = `/${type}/${slug}`;
      const displayName = linkText || formatSlugAsTitle(slug);

      let typeLabel: string;
      let embedClass: string;
      let icon: string;

      switch (type) {
        case 'home':
          typeLabel = 'Punto de reciclaje';
          embedClass = 'point-embed';
          icon = '📍';
          break;
        case 'material':
          typeLabel = 'Material';
          embedClass = 'material-embed';
          icon = '♻️';
          break;
        case 'articulo':
          typeLabel = 'Artículo';
          embedClass = 'article-embed';
          icon = '📄';
          break;
        default:
          return _match;
      }

      return `<div class="internal-embed ${embedClass}" data-type="${type}" data-slug="${slug}" data-path="${path}"><span class="embed-icon">${icon}</span><span class="embed-name">${displayName}</span><span class="embed-type">${typeLabel}</span></div>`;
    });
  }

  // --- Embed Card Population ---

  private scheduleEmbedPopulation(): void {
    if (!this.isBrowser) return;
    clearTimeout(this._embedTimer);
    this._embedTimer = setTimeout(() => this.populateEmbedCards(), 200);
  }

  private populateEmbedCards(): void {
    this.destroyDynamicComponents();
    const embeds = this.elementRef.nativeElement.querySelectorAll('.internal-embed:not(.embed-loaded)');
    embeds.forEach((embed: HTMLElement) => {
      const type = embed.getAttribute('data-type');
      const slug = embed.getAttribute('data-slug');
      if (!type || !slug) return;

      const cacheKey = `${type}/${slug}`;
      if (this.entityCache.has(cacheKey)) {
        this.mountCardComponent(embed, type, this.entityCache.get(cacheKey));
        return;
      }

      switch (type) {
        case 'material':
          this.fetchAndRenderMaterial(slug, embed);
          break;
        case 'home':
          this.fetchAndRenderPoint(slug, embed);
          break;
        case 'articulo':
          this.fetchAndRenderArticle(slug, embed);
          break;
      }
    });
  }

  private fetchAndRenderMaterial(slug: string, embed: HTMLElement): void {
    const materials = materialsState.availableMaterials();
    const material = materials.find(m => m.slug === slug)
      || materials.find(m => m.id === slug);

    if (material) {
      this.entityCache.set(`material/${slug}`, material);
      this.mountCardComponent(embed, 'material', material);
    } else {
      embed.classList.add('embed-loaded');
    }
  }

  private fetchAndRenderPoint(slug: string, embed: HTMLElement): void {
    this.pointsApiService.getPointById(slug).subscribe({
      next: (point: any) => {
        this.entityCache.set(`home/${slug}`, point);
        this.mountCardComponent(embed, 'home', point);
      },
      error: () => {
        embed.classList.add('embed-loaded');
      }
    });
  }

  private fetchAndRenderArticle(slug: string, embed: HTMLElement): void {
    this.articleService.getBySlug(slug).subscribe({
      next: (article) => {
        this.entityCache.set(`articulo/${slug}`, article);
        this.mountCardComponent(embed, 'articulo', article);
      },
      error: () => {
        embed.classList.add('embed-loaded');
      }
    });
  }

  private mountCardComponent(embed: HTMLElement, type: string, data: any): void {
    embed.classList.add('embed-loaded');
    embed.innerHTML = '';

    let ref: ComponentRef<any> | null = null;

    switch (type) {
      case 'home':
        ref = createComponent(RecyclingPointCardComponent, {
          environmentInjector: this.envInjector,
        });
        ref.setInput('point', data);
        ref.setInput('variant', 'compact');
        ref.setInput('showClickable', false);
        break;
      case 'material':
        ref = createComponent(MaterialCardComponent, {
          environmentInjector: this.envInjector,
        });
        ref.setInput('material', data);
        ref.setInput('showActions', false);
        ref.setInput('showClickable', false);
        break;
      case 'articulo':
        ref = createComponent(ArticleCardComponent, {
          environmentInjector: this.envInjector,
        });
        ref.setInput('article', data);
        ref.setInput('showClickable', false);
        break;
    }

    if (ref) {
      this.appRef.attachView(ref.hostView);
      embed.appendChild(ref.location.nativeElement);
      this.dynamicRefs.push(ref);
    }
  }

  /** Lazy-load turndown only in the browser (avoids NG0205 during prerender) */
  private async getTurndownService(): Promise<TurndownService> {
    if (!this.turndownService) {
      const { default: TurndownServiceCtor } = await import('turndown');
      this.turndownService = new TurndownServiceCtor({
        headingStyle: 'atx',
        codeBlockStyle: 'fenced',
        emDelimiter: '*',
        strongDelimiter: '**',
      });
    }
    return this.turndownService;
  }

  ngAfterViewInit() {
    if (this.collapsibleHeaders) {
      setTimeout(() => {
        this.addChevronsToH1();
      }, 100);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Re-resolve when articleMap changes (async slug lookup completes)
    if (changes['articleMap'] && !changes['articleMap'].firstChange && this._rawData) {
      this.resolveAndSetData(this._rawData);
    }

    if (changes['data'] && !changes['data'].firstChange) {
      // Only update if not currently editing
      if (!this.isEditing) {
        if (this.collapsibleHeaders) {
          setTimeout(() => {
            this.addChevronsToH1();
          }, 100);
        }
      }
    }
  }

  private addChevronsToH1() {
    if (!this.isBrowser) return;
    const h1Elements = this.elementRef.nativeElement.querySelectorAll('.markdown-viewer h1');
    h1Elements.forEach((h1: HTMLElement) => {
      if (!h1.querySelector('.chevron')) {
        const chevron = document.createElement('img');
        chevron.className = 'chevron icon-gray-700 icon-20';
        chevron.src = 'assets/icons/chevron-up.svg';
        chevron.alt = 'Toggle section';
        h1.appendChild(chevron);
      }
    });
  }

  onMarkdownClick(event: MouseEvent) {
    const target = event.target as HTMLElement;

    // Check if clicked inside an internal embed card (div-based, not anchor)
    const embedDiv = target.closest('.internal-embed') as HTMLElement;
    if (embedDiv) {
      // Don't intercept clicks on inner anchors (e.g. point-card <a>, material-pill <a>)
      const innerAnchor = target.closest('a');
      if (innerAnchor && embedDiv.contains(innerAnchor)) {
        event.preventDefault();
        const href = innerAnchor.getAttribute('href') || '';
        if (href) {
          this.swnav.navigate(href);
        }
        return;
      }
      // Click on the embed wrapper itself → navigate via data-path
      const path = embedDiv.getAttribute('data-path');
      if (path) {
        this.swnav.navigate(path);
      }
      return;
    }
    
    // Check if clicked on a link (or a child inside a link)
    const anchor = target.tagName === 'A' ? target : target.closest('a');
    if (anchor) {
      event.preventDefault();
      const href = (anchor as HTMLAnchorElement).getAttribute('href') || '';
      const slug = (anchor as HTMLElement).getAttribute('data-slug');
      const isWikilink = (anchor as HTMLElement).classList.contains('wikilink');

      if (isWikilink) {
        // Derive slug from href (data-slug may be stripped by sanitizer)
        const wikilinkSlug = slug || href.replace(`${ARTICLE_ROUTE_PREFIX}/`, '');
        if (wikilinkSlug) {
          const anchorText = (anchor as HTMLElement).textContent || '';
          const defaultTitle = formatSlugAsTitle(wikilinkSlug);
          this.openPopup({
            slug: wikilinkSlug,
            displayText: anchorText !== defaultTitle ? anchorText : null,
            anchorEl: anchor as HTMLElement,
            editable: this.editable
          });
          return;
        }
      }

      // Regular link
      if (href) {
        if (this.openLinksInNewTab && this.isBrowser) {
          window.open(href, '_blank', 'noopener,noreferrer');
        }
        this.linkClick.emit(href);
      }
      return;
    }
    
    // Check if clicked on h1 or its chevron (only if collapsible)
    if (this.collapsibleHeaders) {
      const h1 = target.tagName === 'H1' ? target : target.closest('h1');
      if (h1) {
        this.toggleSection(h1 as HTMLElement);
      }
    }
  }

  private toggleSection(h1Element: HTMLElement) {
    const isCurrentlyCollapsed = h1Element.getAttribute('data-collapsed') === 'true';
    const newCollapsedState = !isCurrentlyCollapsed;
    
    h1Element.setAttribute('data-collapsed', String(newCollapsedState));
    
    const chevron = h1Element.querySelector('.chevron') as HTMLElement;
    if (chevron) {
      chevron.style.transform = newCollapsedState ? 'rotate(180deg)' : 'rotate(0deg)';
    }

    let sibling = h1Element.nextElementSibling;
    while (sibling && sibling.tagName !== 'H1') {
      const htmlSibling = sibling as HTMLElement;
      htmlSibling.style.display = newCollapsedState ? 'none' : '';
      sibling = sibling.nextElementSibling;
    }
  }

  async onContentInput(event: Event) {
    if (!this.editable || !this.isBrowser) return;
    
    this.isEditing = true;
    
    const target = event.target as HTMLElement;
    const html = target.innerHTML;
    
    // Convert HTML back to markdown (lazy-loaded)
    const turndown = await this.getTurndownService();
    const markdown = turndown.turndown(html);
    
    // Emit the change
    this.contentChange.emit(markdown);
  }

  onBlur() {
    // Mark editing as complete when user leaves the field
    this.isEditing = false;
  }

  onKeyDown(event: KeyboardEvent) {
    if (!this.editable || !this.isBrowser) return;

    // Handle formatting shortcuts
    if (event.ctrlKey || event.metaKey) {
      switch (event.key.toLowerCase()) {
        case 'b':
          event.preventDefault();
          document.execCommand('bold', false);
          break;
        case 'i':
          event.preventDefault();
          document.execCommand('italic', false);
          break;
      }
    }
  }

  // --- Wikilink Popup ---

  openPopup(data: WikilinkPopupData): void {
    // Toggle: close if same wikilink is clicked
    const current = this.popupData();
    if (current && current.slug === data.slug && current.anchorEl === data.anchorEl) {
      this.closePopup();
      return;
    }
    this.closePopup();
    this.popupData.set(data);
  }

  closePopup(): void {
    this.popupData.set(null);
  }

  onPopupNavigate(slug: string): void {
    this.closePopup();
    this.router.navigateByUrl(`${ARTICLE_ROUTE_PREFIX}/${slug}`);
  }

  onPopupCreate(slug: string): void {
    this.closePopup();
    this.wikilinkCreate.emit(slug);
  }

  onPopupChanged(change: WikilinkChange): void {
    if (!this.editable) return;
    this.updateWikilinkInRaw(change);
  }

  onPopupUnlink(slug: string): void {
    if (!this.editable) return;
    this.removeWikilinkFromRaw(slug);
    this.closePopup();
  }

  private updateWikilinkInRaw(change: WikilinkChange): void {
    const escapedSlug = change.oldSlug.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`\\[\\[${escapedSlug}(?:\\|[^\\]]+)?\\]\\]`, 'g');
    const replacement = buildWikilink(change.newSlug, change.newDisplayText || undefined);
    this._rawData = this._rawData.replace(regex, replacement);
    this.resolveAndSetData(this._rawData);
    this.contentChange.emit(this._rawData);
  }

  private removeWikilinkFromRaw(slug: string): void {
    const escapedSlug = slug.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`\\[\\[${escapedSlug}(?:\\|([^\\]]+))?\\]\\]`, 'g');
    this._rawData = this._rawData.replace(regex, (_match: string, customText?: string) => {
      return customText || formatSlugAsTitle(slug);
    });
    this.resolveAndSetData(this._rawData);
    this.contentChange.emit(this._rawData);
  }
}
